/**
 * Round-trip helpers for the organisation's
 * `tmsOrderReferenceLabelsJson` column. Used by:
 *  - {@link OrderReferenceLabelsEditor} on the Settings → Organisation
 *    page to edit the per-org label map.
 *  - The Orders → Order Details page's References panel to render the
 *    operator-facing label for each `Order.Reference${i}` slot.
 *
 * <p>
 * Shape on the wire is a JSON OBJECT keyed by slot name —
 * `{ "Reference1": "Customer PO", "Reference2": "Internal Job" }`. The
 * BE's `UpdateTmsOrderReferenceLabelsHandler` rejects non-object JSON
 * (arrays / primitives) with 422, so these helpers can rely on the
 * column being object-shaped on read.
 * </p>
 */

/**
 * Minimum number of label rows the editor surfaces and the orders
 * page renders. Even when the org hasn't configured anything yet, we
 * always show two reference slots so the Order Details page never
 * renders a one-cell "References" grid.
 */
export const ORDER_REF_LABELS_MIN_ROWS = 2;

/**
 * Cap on the number of label rows. Anything beyond slot 4 lives in
 * the order's `additionalReferencesJson` long-tail on the BE; the
 * first four are real indexed columns. Ten is plenty; raise it later
 * if a customer asks.
 */
export const ORDER_REF_LABELS_MAX_ROWS = 10;

/**
 * Parse the organisation's `tmsOrderReferenceLabelsJson` column into a
 * positional array. Index `i` corresponds to slot `Reference${i + 1}`
 * on the order. Always returns at least {@link ORDER_REF_LABELS_MIN_ROWS}
 * entries; trailing empty rows past the minimum are trimmed so a
 * re-hydration after save doesn't grow phantom rows.
 *
 * <p>
 * Tolerant of malformed input: anything that isn't a plain JSON
 * object collapses to a two-empty-row default. The BE handler rejects
 * malformed payloads on write, so the only way junk lands in the
 * column is via a direct DB edit — we still want the page to render.
 * </p>
 */
export function parseOrderReferenceLabels(json: string | null | undefined): string[] {
  const fallback = Array.from({ length: ORDER_REF_LABELS_MIN_ROWS }, () => '');
  if (!json) return fallback;
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return fallback;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return fallback;
  }

  // Discover the highest slot index present so we round-trip even if
  // the operator only filled slot 3 (Reference3) and skipped slot 1.
  const obj = parsed as Record<string, unknown>;
  const slotNumbers: number[] = [];
  for (const key of Object.keys(obj)) {
    const m = /^Reference(\d+)$/.exec(key);
    if (!m) continue;
    const n = Number(m[1]);
    if (Number.isFinite(n) && n >= 1) slotNumbers.push(n);
  }
  const maxSlot = Math.max(ORDER_REF_LABELS_MIN_ROWS, ...slotNumbers);

  const out: string[] = [];
  for (let i = 1; i <= maxSlot; i++) {
    const v = obj[`Reference${i}`];
    out.push(typeof v === 'string' ? v : '');
  }

  // Trim trailing blank rows down to the minimum so we don't keep
  // re-rendering empty slots the operator removed in an earlier pass.
  // The length guard makes the index access safe at runtime; the
  // `?? ''` keeps TS happy under `noUncheckedIndexedAccess`.
  while (out.length > ORDER_REF_LABELS_MIN_ROWS && !(out[out.length - 1] ?? '').trim()) {
    out.pop();
  }
  return out;
}

/**
 * Serialise the positional array back to the JSON shape the BE
 * expects (`{ "Reference1": "...", "Reference2": "..." }`). Blank
 * rows are dropped — they don't earn a key in the column.
 *
 * <p>
 * Returns `null` when every row is blank so the caller can hand it
 * straight to the update endpoint, which treats `null` as "clear".
 * </p>
 */
export function serialiseOrderReferenceLabels(labels: string[]): string | null {
  const obj: Record<string, string> = {};
  for (let i = 0; i < labels.length; i++) {
    const v = labels[i]?.trim();
    if (v) obj[`Reference${i + 1}`] = v;
  }
  if (Object.keys(obj).length === 0) return null;
  return JSON.stringify(obj);
}

/**
 * Operator-facing label for slot `i` (0-indexed). Falls back to
 * `Reference ${i + 1}` when the org hasn't configured a label for
 * that slot — used by both the editor's preview line and the Orders
 * page's reference inputs so the fallback text is identical in both
 * places.
 */
export function resolveOrderReferenceLabel(labels: string[], slotIndex: number): string {
  const configured = labels[slotIndex]?.trim();
  if (configured) return configured;
  return `Reference ${slotIndex + 1}`;
}

// ────────────────────────────────────────────────────────────────────────────
// Per-CUSTOMER required slots (round-trip with
// Party.TmsRequiredOrderReferencesJson in Directory)
//
// A customer may insist on some of the slots the organisation configured — a
// haulier's customer who will not pay an invoice without their PO number on it.
// Stored as 1-based slot NUMBERS for the same reason the workflow condition is:
// the labels are renameable, and a stored label would silently stop matching.
//
// ADVISORY. The order screen marks these fields and warns when they are blank;
// nothing refuses a save or a status move over them. Hard gates live in
// Settings → Workflows, which applies to every customer alike.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Parse `Party.tmsRequiredOrderReferencesJson` into 1-based slot numbers,
 * distinct and ascending.
 *
 * <p>
 * Tolerant in the same way {@link parseOrderReferenceLabels} is: anything that
 * is not an array of sane slot numbers collapses to "none required". A rule
 * nobody can read is better dropped than guessed at — guessing would put an
 * asterisk on a field the customer never asked for.
 * </p>
 */
export function parseRequiredOrderReferenceSlots(json: string | null | undefined): number[] {
  if (!json) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  const out = new Set<number>();
  for (const raw of parsed) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > ORDER_REF_LABELS_MAX_ROWS) continue;
    out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

/**
 * Serialise picked slots back to the column's shape, or `null` when none are
 * picked — which is how the endpoint spells "require nothing".
 *
 * <p>
 * Normalised (distinct, ascending) to match what the BE stores, so a caller can
 * compare this against the stored string to decide whether anything actually
 * changed, rather than issuing a write on every save.
 * </p>
 */
export function serialiseRequiredOrderReferenceSlots(slots: readonly number[]): string | null {
  const clean = parseRequiredOrderReferenceSlots(JSON.stringify([...slots]));
  return clean.length === 0 ? null : JSON.stringify(clean);
}

// ────────────────────────────────────────────────────────────────────────────
// Per-order reference VALUES (round-trip with Order.Reference1..4 +
// AdditionalReferencesJson on the BE)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Subset of `Order` carrying the reference data — typed against the
 * `@my/tms-api` shape but kept structural so this util doesn't have a
 * runtime dependency on the package and stays testable in isolation.
 */
export interface OrderReferenceSource {
  reference1?: string | null;
  reference2?: string | null;
  reference3?: string | null;
  reference4?: string | null;
  additionalReferencesJson?: string | null;
}

/**
 * Extract the per-slot reference values out of an order's persisted
 * shape into a positional array. Slots 1..4 come from the dedicated
 * `Reference1..4` columns; slot 5+ are pulled out of the
 * `additionalReferencesJson` long-tail.
 *
 * <p>
 * The returned array is sized to <c>max(slotCount, MIN_ROWS,
 * highestPersistedSlot)</c>. Trailing blanks above <c>slotCount</c>
 * are trimmed — but persisted values past <c>slotCount</c> are
 * preserved so the operator can still see / clear them if the org's
 * label config shrank after the order was created.
 * </p>
 */
export function extractOrderReferenceValues(
  source: OrderReferenceSource | null | undefined,
  slotCount: number,
): string[] {
  const floor = Math.max(slotCount, ORDER_REF_LABELS_MIN_ROWS);

  if (!source) {
    return Array.from({ length: floor }, () => '');
  }

  // Indexed columns first. Anything past index 3 (slot 4) comes from
  // the long-tail JSON below.
  const out: string[] = [
    source.reference1 ?? '',
    source.reference2 ?? '',
    source.reference3 ?? '',
    source.reference4 ?? '',
  ];

  // Pull Reference5+ from the long-tail JSON. Malformed JSON falls
  // through silently — the BE's update handler rejects bad payloads
  // on write, so junk in the column would only come from a direct DB
  // edit, and we still want the page to render.
  if (source.additionalReferencesJson) {
    let parsed: unknown = null;
    try {
      parsed = JSON.parse(source.additionalReferencesJson);
    } catch {
      parsed = null;
    }
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const obj = parsed as Record<string, unknown>;
      let maxSlot = 4;
      for (const key of Object.keys(obj)) {
        const m = /^Reference(\d+)$/.exec(key);
        if (!m) continue;
        const n = Number(m[1]);
        if (Number.isFinite(n) && n > maxSlot) maxSlot = n;
      }
      while (out.length < maxSlot) out.push('');
      for (let i = 5; i <= maxSlot; i++) {
        const v = obj[`Reference${i}`];
        out[i - 1] = typeof v === 'string' ? v : '';
      }
    }
  }

  // Pad up to the floor so every labelled slot has an entry to bind
  // to (even when the order itself has nothing in that slot yet).
  while (out.length < floor) out.push('');

  // Trim trailing blanks down to the floor — keeps the form from
  // rendering empty slots beyond the org's configured count when
  // there's no persisted data to justify them.
  while (out.length > floor && !(out[out.length - 1] ?? '').trim()) {
    out.pop();
  }

  return out;
}

/**
 * Free-form per-order reference entry. Unlike the positional slots
 * (whose labels come from the org config), a free-form extra carries
 * its own operator-typed label that travels INSIDE the order's
 * `additionalReferencesJson` blob — the label IS the JSON key.
 *
 * <p>
 * Restored after the org-driven slots landed because some operators
 * want a one-off reference for a specific order ("GMR confirmation",
 * "Driver instruction code") that the org-level label config
 * shouldn't carry forever. The extras share the same column as the
 * slot-indexed entries; they're distinguished by key shape:
 *   - <c>"Reference5": "value"</c> → positional slot
 *   - <c>"Customs Ref": "GMR-12345"</c> → free-form extra (label-as-key)
 * </p>
 */
export interface OrderReferenceExtra {
  /** Operator-typed label. Acts as the JSON key on persist. */
  label: string;
  value: string;
}

/**
 * Pull the free-form entries out of an order's
 * `additionalReferencesJson`. Skips anything matching the slot-
 * indexed `ReferenceN` pattern (those belong to the positional
 * values array via {@link extractOrderReferenceValues}). Tolerant of
 * malformed input — returns an empty array rather than throwing.
 */
export function extractOrderReferenceExtras(
  source: OrderReferenceSource | null | undefined,
): OrderReferenceExtra[] {
  if (!source?.additionalReferencesJson) return [];
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(source.additionalReferencesJson);
  } catch {
    return [];
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return [];
  const obj = parsed as Record<string, unknown>;
  const out: OrderReferenceExtra[] = [];
  for (const [key, raw] of Object.entries(obj)) {
    // Slot-indexed entries belong to the positional values array, not
    // the extras list. The regex matches `Reference` followed by ≥1
    // digit only — `ReferenceCust` etc. are still treated as labels.
    if (/^Reference\d+$/.test(key)) continue;
    if (typeof raw !== 'string') continue;
    out.push({ label: key, value: raw });
  }
  return out;
}

/** Slots split out for the order save payload — slots 1..4 are indexed columns; the rest is JSON. */
export interface OrderReferenceSavePayload {
  reference1: string | null;
  reference2: string | null;
  reference3: string | null;
  reference4: string | null;
  /**
   * JSON object. Two kinds of entries coexist in here:
   *   - Slot-indexed (`Reference5`..`ReferenceN`) — continuation of
   *     the org's labelled slots beyond `Reference4`.
   *   - Free-form (any non-`ReferenceN` key) — order-specific extras
   *     whose label is operator-typed and lives as the JSON key.
   * `null` when neither kind has content.
   */
  additionalReferencesJson: string | null;
}

/**
 * Build the order save payload from the positional slot values + the
 * free-form extras. Blank inputs collapse to `null` (column) or are
 * dropped (JSON) so cleared fields stop cleanly at the BE boundary.
 *
 * <p>
 * Free-form extras whose label looks like a slot identifier (`Reference5`)
 * are skipped to avoid silently shadowing a positional entry on the
 * next read. The Add-reference UI should reject those at input time
 * too, but this is the last line of defence.
 * </p>
 */
export function toOrderReferenceSavePayload(
  values: string[],
  extras: OrderReferenceExtra[] = [],
): OrderReferenceSavePayload {
  const trimmedValues = values.map((v) => (v ?? '').trim());
  const json: Record<string, string> = {};

  // Slot-indexed entries — Reference5+.
  for (let i = 4; i < trimmedValues.length; i++) {
    const v = trimmedValues[i];
    if (v) json[`Reference${i + 1}`] = v;
  }

  // Free-form entries — label becomes the JSON key. Reject blanks and
  // anything that collides with the slot pattern.
  for (const extra of extras) {
    const label = extra.label.trim();
    const value = extra.value.trim();
    if (!label || !value) continue;
    if (/^Reference\d+$/.test(label)) continue;
    // Last-write-wins on duplicate labels — Object.entries preserves
    // insertion order so the most recently added wins. Add-time
    // validation should prevent dupes in normal flow.
    json[label] = value;
  }

  return {
    reference1: trimmedValues[0] || null,
    reference2: trimmedValues[1] || null,
    reference3: trimmedValues[2] || null,
    reference4: trimmedValues[3] || null,
    additionalReferencesJson: Object.keys(json).length === 0 ? null : JSON.stringify(json),
  };
}
